import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { ppmpDepartmentsApi } from '../procurementPlansApi';

/**
 * Enrols one organizational unit as a PPMP department.
 *
 * **Create only — there is no edit.** The row holds a link to an org unit and nothing else, so
 * there is nothing on it to correct: moving the roster to a different office is a withdrawal
 * and an enrolment, which is why the row menu offers exactly those two.
 */
export function PpmpDepartmentModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [orgUnitId, setOrgUnitId] = useState('');
  const [units, setUnits] = useState<SelectOption[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    ppmpDepartmentsApi
      .candidates(controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;

        /*
          Only units NOT already enrolled come back, so the picker cannot offer a value the
          server will refuse. The PATH is the sublabel because two offices in different
          divisions may share a name, and the name alone would not say which is which.
        */
        setUnits(
          rows.map((u) => ({
            value: u.orgUnitId,
            label: u.name,
            sublabel: u.path ?? undefined,
            right: u.orgUnitTypeName ?? undefined,
            // The tier and the path are searchable without the reader having to read them.
            keywords: [u.path, u.orgUnitTypeName].filter(Boolean).join(' '),
          })),
        );
      })
      .catch(() => {
        // Fails soft — the save still refuses an unknown unit, so the server stays the
        // authority on what may be enrolled.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingUnits(false);
      });

    return () => controller.abort();
  }, []);

  const canSave = orgUnitId.length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await ppmpDepartmentsApi.create({ orgUnitId });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not enrol that department.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ppmp-department' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enrol a PPMP department"
      size="lg"
      id="ppmp-department-modal"
      autogenerate={{
        /*
          Fills the one required field from the live candidate list — a real unit, never an
          invented id, which the server would refuse and which would read as a bug in the form.
          With no candidates left there is nothing to generate.
        */
        onGenerate: () => {
          if (units.length > 0) {
            setOrgUnitId(units[Math.floor(Math.random() * units.length)].value);
          }
        },
        disabled: saving || units.length === 0,
      }}
      confirm={{
        id: 'ppmp-department-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppmp-department-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppmp-department-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="ppmp-department-org-unit-id"
          label="Organizational unit"
          required
          description="The office that will file a procurement plan. It then appears on every annual procurement plan's roster, whether or not it has filed anything."
          value={orgUnitId || null}
          onChange={setOrgUnitId}
          options={units}
          loading={loadingUnits}
          clearable={false}
          disabled={saving}
          error={error?.fieldError('orgUnitId')}
          /*
            NO create-in-place. An org unit belongs to the Organization module and is made on
            its chart, with a parent and a tier this form cannot ask for — `allowCustom` writes
            a row from typed text alone and could supply neither.
          */
        />

        {/*
          The one thing the picker cannot say: what happens when the list is empty. An empty
          required picker with no explanation reads as a broken form.
        */}
        {!loadingUnits && units.length === 0 ? (
          <p className="text-xs text-slate-500">
            Every active organizational unit is already enrolled. Add a unit on the organizational
            chart, or restore a withdrawn department from this list.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
